import {
  Form,
  Input,
  Button,
  InputNumber,
  Card,
  Table,
  Select,
  Checkbox,
  Row,
  Col,
  Tag,
  message,
  Drawer,
  Space,
  Switch,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  FetchVehicleType_api,
  UpdateInquiry_api,
  FetchDealers_api,
  FetchSalesPersons_api,
} from "../../../Api";
import { FetchAvailableVehicles_api } from "../../../Api/Stock/stock_api";
import { formatNumber } from "../../../lib/common";
import { useAuthChecker } from "../../../Context";
import config from "../../../Constant/config";

const { Option } = Select;

export const ManageCustomer = ({
  submitting,
  form,
  handleFinish,
  initialValues,
}: any) => {
  const { user } = useAuthChecker();
  const isEditMode = initialValues && initialValues.id ? true : false;
  const [vehicleTypeData, setVehicleTypeData] = useState<any[]>([]);

  const watchedInquiries = Form.useWatch("inquiries", form);

  const [existingInquiries, setExistingInquiries] = useState<any[]>([]);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingInquiry, setEditingInquiry] = useState<any>(null);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [selectedDealer, setSelectedDealer] = useState<any>(null);
  const [editForm] = Form.useForm();

  const [editAvailableVehicles, setEditAvailableVehicles] = useState<any[]>([]);
  const [isFetchingEditVehicles, setIsFetchingEditVehicles] = useState(false);

  useEffect(() => {
    if (initialValues?.inquiries) {
      setExistingInquiries(initialValues.inquiries);
      form.setFieldsValue({ inquiries: [] });
    } else {
      setExistingInquiries([]);
    }
  }, [initialValues]);

  // New Inquiry Modal State
  const [newInquiryModalOpen, setNewInquiryModalOpen] = useState(false);
  const [editingNewInquiryIndex, setEditingNewInquiryIndex] = useState<
    number | null
  >(null);
  const [newInquiryForm] = Form.useForm();
  const [modalAvailableVehicles, setModalAvailableVehicles] = useState<any[]>(
    [],
  );
  const [isFetchingModalVehicles, setIsFetchingModalVehicles] = useState(false);

  const [isCalculatingNew, setIsCalculatingNew] = useState(false);
  const [isCalculatingEdit, setIsCalculatingEdit] = useState(false);
  const [newCalcTimer, setNewCalcTimer] = useState<any>(null);
  const [editCalcTimer, setEditCalcTimer] = useState<any>(null);

  const watchedNewInquiryTotal = Form.useWatch("total", newInquiryForm);
  const watchedNewInquiryDownpayment = Form.useWatch(
    "downpayment",
    newInquiryForm,
  );
  const watchedNewInquiryVehicle = Form.useWatch("vehicleId", newInquiryForm);

  const watchedEditInquiryTotal = Form.useWatch("totalamount", editForm);
  const watchedEditInquiryDownpayment = Form.useWatch("downpayment", editForm);
  const watchedEditInquiryVehicle = Form.useWatch("vehicleId", editForm);

  const { isFetching: isFetchingVehicleType } = useQuery({
    queryKey: ["vehicleTypes"],
    queryFn: async () => {
      const response: any = await FetchVehicleType_api();
      setVehicleTypeData(response?.vehicleData || []);
      return response;
    },
    enabled: true,
    refetchOnWindowFocus: false,
  });

  const [dealers, setDealers] = useState<any[]>([]);
  const { isFetching: isFetchingDealers } = useQuery({
    queryKey: ["dealersList"],
    queryFn: async () => {
      const response: any = await FetchDealers_api(1, 100);
      setDealers(response?.dealerData || []);
      return response;
    },
    enabled: true,
    refetchOnWindowFocus: false,
  });

  const [salesPersons, setSalesPersons] = useState<any[]>([]);
  const { isFetching: isFetchingSalesPersons } = useQuery({
    queryKey: ["salesPersonsList"],
    queryFn: async () => {
      const response: any = await FetchSalesPersons_api(1, 100, "");
      setSalesPersons(response?.data || []);
      return response;
    },
    enabled: true,
    refetchOnWindowFocus: false,
  });

  const fetchVehiclesForEdit = async (
    typeId: number,
    modelId: number,
    currentVehicleId: number | null = null,
    currentRecord: any = null,
  ) => {
    if (!typeId || !modelId) {
      setEditAvailableVehicles([]);
      return;
    }

    setIsFetchingEditVehicles(true);
    try {
      const res = await FetchAvailableVehicles_api(1, 50, {
        type_id: typeId,
        modal_id: modelId,
        dealer_id: selectedDealer,
      });
      let vehicles = res?.data || [];

      if (currentVehicleId) {
        const found = vehicles.find((v: any) => v.id === currentVehicleId);
        if (!found) {
          if (currentRecord) {
            vehicles = [
              {
                id: currentVehicleId,
                engine_number: currentRecord.engine_number,
                chassis_number: currentRecord.chassis_number,
                color_name: currentRecord.vehicle_color,
              },
              ...vehicles,
            ];
          }
        }
      }
      setEditAvailableVehicles(vehicles);
    } catch (e) {
      console.error(e);
      setEditAvailableVehicles([]);
    } finally {
      setIsFetchingEditVehicles(false);
    }
  };

  const calculateEditTotal = () => {
    if (editCalcTimer) clearTimeout(editCalcTimer);
    setIsCalculatingEdit(true);

    const timer = setTimeout(() => {
      const { vehicleType, vehicleModel, selectedPriceItems, downpayment } =
        editForm.getFieldsValue();
      if (!vehicleType || !vehicleModel || !selectedPriceItems) {
        setIsCalculatingEdit(false);
        return;
      }

      const type = vehicleTypeData.find((v) => v.id === vehicleType);
      const model = type?.models.find((m: any) => m.id === vehicleModel);

      if (model) {
        let subtotal = 0;
        selectedPriceItems.forEach((key: string) => {
          if (model[key] && key !== "document_charge") {
            subtotal += parseFloat(model[key] || 0);
          }
        });

        const dp = parseFloat(downpayment || 0);
        // Temporary override: disable process, just save the value
        const isBatapola = false; // editForm.getFieldValue("isBatapola");
        let selected = editForm.getFieldValue("selectedPriceItems") || [];
        let docCharge = 0;
        let loanAmount = 0;

        if (isBatapola) {
          const gap = subtotal - dp;
          const threshold = config.batapolaThreshold || 500000;
          const flatFee = config.batapolaFlatFee || 25000;
          const rate = config.batapolaRate || 0.05;

          if (gap >= threshold - flatFee) {
            docCharge = flatFee;
            loanAmount = gap + flatFee;
          } else if (gap > 0) {
            loanAmount = gap / (1 - rate);
            docCharge = loanAmount * rate;
          } else {
            docCharge = 0;
            loanAmount = 0;
          }

          if (!selected.includes("document_charge")) {
            editForm.setFieldValue("selectedPriceItems", [
              ...selected,
              "document_charge",
            ]);
          }
        } else {
          docCharge = 0;
          loanAmount = subtotal - dp;
          if (selected.includes("document_charge")) {
            editForm.setFieldValue(
              "selectedPriceItems",
              selected.filter((item: string) => item !== "document_charge"),
            );
          }
        }

        if (loanAmount < 0 || dp <= 0) loanAmount = 0;
        const total = subtotal + docCharge;

        editForm.setFieldsValue({
          totalamount: parseFloat(total.toFixed(2)),
          loanamount: parseFloat(loanAmount.toFixed(2)),
        });
      }
      setIsCalculatingEdit(false);
    }, 50);

    setEditCalcTimer(timer);
  };

  const handleEditInquiry = async (record: any) => {
    setEditingInquiry(record);

    const priceKeys = record.priceList ? Object.keys(record.priceList) : [];

    editForm.setFieldsValue({
      inquiryNumber: record.inquiryNumber,
      status: record.status || "Pending",
      totalamount: record.total,
      vehicleType: record.vehicleTypeId,
      vehicleModel: record.vehicleModelId,
      vehicleId: record.vehicleId,
      salesPersonId: record.salesPersonId,
      selectedPriceItems: priceKeys,
      downpayment: record.downpayment,
      loanamount: record.loanamount,
      isBatapola:
        record.isBatapola == 1 || record.isBatapola === true ? true : false,
    });

    if (record.vehicleTypeId && record.vehicleModelId) {
      await fetchVehiclesForEdit(
        record.vehicleTypeId,
        record.vehicleModelId,
        record.vehicleId,
        record,
      );
    } else {
      setEditAvailableVehicles([]);
    }

    setEditModalOpen(true);
  };

  const handleUpdateInquiry = async (values: any) => {
    setUpdateLoading(true);
    try {
      const type = vehicleTypeData.find((v) => v.id === values.vehicleType);
      const model = type?.models.find((m: any) => m.id === values.vehicleModel);
      const priceListObj: Record<string, number> = {};

      if (model && values.selectedPriceItems) {
        values.selectedPriceItems.forEach((key: string) => {
          priceListObj[key] = parseFloat(model[key] || 0);
        });
      }

      const isBatapola = values.isBatapola || false;
      // Temporary override: disable process, just save the value
      const processBatapola = false; // isBatapola
      if (processBatapola) {
        let baseTotal = 0;
        Object.values(priceListObj).forEach((val) => (baseTotal += val));
        const threshold = config.batapolaThreshold || 500000;
        const flatFee = config.batapolaFlatFee || 25000;
        const rate = config.batapolaRate || 0.05;

        const gap = baseTotal - values.downpayment;
        let docCharge = 0;
        if (gap >= threshold - flatFee) {
          docCharge = flatFee;
        } else if (gap > 0) {
          docCharge = (gap / (1 - rate)) * rate;
        }
        priceListObj["document_charge"] = docCharge;
      }

      const payload = {
        id: editingInquiry.id,
        userId: user?.userId,
        inquiryNumber: values.inquiryNumber,
        status: values.status,
        totalamount: values.totalamount,
        vehicleType: values.vehicleType,
        vehicleModel: values.vehicleModel,
        vehicleId: values.vehicleId,
        salesPersonId: values.salesPersonId,
        priceList: priceListObj,
        isBatapola: isBatapola,
        downpayment: values.downpayment,
        loanamount: values.loanamount,
      };

      const res = await UpdateInquiry_api(payload);
      if (res.isDataUpdated) {
        message.success("Inquiry updated successfully!");
        setEditModalOpen(false);
        setExistingInquiries((prev) =>
          prev.map((item) =>
            item.id === editingInquiry.id
              ? {
                  ...item,
                  inquiryNumber: values.inquiryNumber,
                  total: values.totalamount,
                  status: values.status,
                  vehicleTypeId: values.vehicleType,
                  vehicleModelId: values.vehicleModel,
                  vehicleId: values.vehicleId,
                  // Update Names if possible (Find from metadata)
                  vehicleType:
                    vehicleTypeData.find((v) => v.id === values.vehicleType)
                      ?.v_type || item.vehicleType,
                  vehicleModel:
                    vehicleTypeData
                      .find((v) => v.id === values.vehicleType)
                      ?.models.find((m: any) => m.id === values.vehicleModel)
                      ?.name || item.vehicleModel,
                  priceList: priceListObj,
                  salesPersonId: values.salesPersonId,
                  downpayment: values.downpayment,
                  loanamount: values.loanamount,
                }
              : item,
          ),
        );
      } else {
        message.error(res.msg || "Update failed");
      }
    } catch (e) {
      console.error(e);
      message.error("Error updating inquiry");
    } finally {
      setUpdateLoading(false);
      setNewInquiryModalOpen(false);
    }
  };

  const fetchVehiclesForNewInquiry = async (
    typeId: number,
    modelId: number,
  ) => {
    if (!typeId || !modelId) {
      setModalAvailableVehicles([]);
      return;
    }

    setIsFetchingModalVehicles(true);
    try {
      const res = await FetchAvailableVehicles_api(1, 50, {
        type_id: typeId,
        modal_id: modelId,
      });
      if (res?.data) {
        setModalAvailableVehicles(res.data);
        if (res.data.length === 0) {
          message.info(
            "No available vehicles found for this type/model in stock.",
          );
        }
      }
    } catch (e) {
      console.error(e);
      setModalAvailableVehicles([]);
    } finally {
      setIsFetchingModalVehicles(false);
    }
  };

  const calculateNewInquiryTotal = () => {
    if (newCalcTimer) clearTimeout(newCalcTimer);
    setIsCalculatingNew(true);

    const timer = setTimeout(() => {
      const { vehicleType, vehicleModel, selectedPriceItems, downpayment } =
        newInquiryForm.getFieldsValue();
      if (!vehicleType || !vehicleModel || !selectedPriceItems) {
        setIsCalculatingNew(false);
        return;
      }

      const type = vehicleTypeData.find((v) => v.id === vehicleType);
      const model = type?.models.find((m: any) => m.id === vehicleModel);

      if (model) {
        let subtotal = 0;
        selectedPriceItems.forEach((key: string) => {
          if (model[key] && key !== "document_charge") {
            subtotal += parseFloat(model[key] || 0);
          }
        });

        const dp = parseFloat(downpayment || 0);
        // Temporary override: disable process, just save the value
        const isBatapola = false; // newInquiryForm.getFieldValue("isBatapola");
        let selected = newInquiryForm.getFieldValue("selectedPriceItems") || [];
        let docCharge = 0;
        let loanAmount = 0;

        if (isBatapola) {
          const gap = subtotal - dp;
          const threshold = config.batapolaThreshold || 500000;
          const flatFee = config.batapolaFlatFee || 25000;
          const rate = config.batapolaRate || 0.05;

          if (gap >= threshold - flatFee) {
            docCharge = flatFee;
            loanAmount = gap + flatFee;
          } else if (gap > 0) {
            loanAmount = gap / (1 - rate);
            docCharge = loanAmount * rate;
          } else {
            docCharge = 0;
            loanAmount = 0;
          }

          if (!selected.includes("document_charge")) {
            newInquiryForm.setFieldValue("selectedPriceItems", [
              ...selected,
              "document_charge",
            ]);
          }
        } else {
          docCharge = 0;
          loanAmount = subtotal - dp;
          if (selected.includes("document_charge")) {
            newInquiryForm.setFieldValue(
              "selectedPriceItems",
              selected.filter((item: string) => item !== "document_charge"),
            );
          }
        }

        const total = subtotal + docCharge;
        if (loanAmount < 0 || dp <= 0) loanAmount = 0;

        newInquiryForm.setFieldsValue({
          total: parseFloat(total.toFixed(2)),
          loanamount: parseFloat(loanAmount.toFixed(2)),
        });
      }
      setIsCalculatingNew(false);
    }, 50);

    setNewCalcTimer(timer);
  };

  const inquiryColumns = [
    {
      title: "Inquiry Number",
      dataIndex: "inquiryNumber",
      key: "inquiryNumber",
    },
    {
      title: "Vehicle Selection",
      key: "vehicle",
      render: (record: any) =>
        record.engine_number ? (
          <div>
            <Tag color="blue">
              {record.vehicleType} - {record.vehicleModel}
            </Tag>
            <br />
            <span className="text-xs text-gray-500">
              ENG: {record.engine_number}
            </span>
            <br />
            <span className="text-xs text-gray-500">
              CHAS: {record.chassis_number}
            </span>
          </div>
        ) : (
          <span>
            {record.vehicleType} - {record.vehicleModel}{" "}
            <Tag>(No specific vehicle)</Tag>
          </span>
        ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status: string) => (
        <Tag
          color={
            status?.toLowerCase() === "paid"
              ? "green"
              : status?.toLowerCase() === "pending"
                ? "orange"
                : "default"
          }
        >
          {status || "Unknown"}
        </Tag>
      ),
    },
    {
      title: "Total Amount (Rs:)",
      dataIndex: "total",
      key: "total",
      render: (text: any) =>
        text !== undefined && text !== null
          ? parseFloat(text).toLocaleString("en-US", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })
          : "0.00",
    },
    {
      title: "Down Payment",
      dataIndex: "downpayment",
      key: "downpayment",
      render: (text: any) =>
        text !== undefined && text !== null
          ? parseFloat(text).toLocaleString("en-US", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })
          : "0.00",
    },
    {
      title: "Loan Amount",
      dataIndex: "loanamount",
      key: "loanamount",
      render: (text: any) =>
        text !== undefined && text !== null
          ? parseFloat(text).toLocaleString("en-US", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })
          : "0.00",
    },
    {
      title: "Price List",
      dataIndex: "priceList",
      key: "priceList",
      render: (text: any) =>
        text !== undefined && text !== null
          ? Object.entries(text).map(([key, value]: any) => (
              <p className="my-0 py-0" key={key}>
                - {key}: {formatNumber(value)}
              </p>
            ))
          : "-",
    },
    {
      title: "Action",
      key: "action",
      render: (_: any, record: any) => (
        <Button
          size="small"
          icon={<EditOutlined />}
          onClick={() => handleEditInquiry(record)}
        >
          Edit
        </Button>
      ),
    },
  ];

  const columns = [
    {
      title: "Inquiry Number",
      dataIndex: "inquiryNumber",
      key: "inquiryNumber",
    },
    {
      title: "Vehicle",
      key: "vehicle",
      render: (_, record: any) => {
        const type = vehicleTypeData.find((v) => v.id == record.vehicleType);
        const model = type?.models?.find(
          (m: any) => m.id == record.vehicleModel,
        );
        return ` ${model?.name || ""}`;
      },
    },
    {
      title: "Amount",
      dataIndex: "total",
      key: "total",
      render: (val) =>
        val
          ? parseFloat(val).toLocaleString("en-US", {
              minimumFractionDigits: 2,
            })
          : "0.00",
    },
    {
      title: "Down Payment",
      dataIndex: "downpayment",
      key: "downpayment",
      render: (val: any) =>
        val
          ? parseFloat(val).toLocaleString("en-US", {
              minimumFractionDigits: 2,
            })
          : "0.00",
    },
    {
      title: "Loan Amount",
      dataIndex: "loanamount",
      key: "loanamount",
      render: (val: any) =>
        val
          ? parseFloat(val).toLocaleString("en-US", {
              minimumFractionDigits: 2,
            })
          : "0.00",
    },
    {
      title: "Action",
      key: "action",
      render: (_, record, index) => (
        <Space>
          <Button
            icon={<EditOutlined />}
            size="small"
            onClick={() => {
              setEditingNewInquiryIndex(index);
              setNewInquiryModalOpen(true);
              newInquiryForm.setFieldsValue(record);
              if (record.vehicleType && record.vehicleModel) {
                fetchVehiclesForNewInquiry(
                  record.vehicleType,
                  record.vehicleModel,
                );
              }
            }}
          />
          <Button
            icon={<DeleteOutlined />}
            danger
            size="small"
            onClick={() => {
              const current = form.getFieldValue("inquiries") || [];
              const newInq = [...current];
              newInq.splice(index, 1);
              form.setFieldsValue({ inquiries: newInq });
              form.validateFields(["inquiries"]);
            }}
          />
        </Space>
      ),
    },
  ];

  const addnquery = () => {
    newInquiryForm.validateFields().then((values) => {
      const current = form.getFieldValue("inquiries") || [];
      const newInq = [...current];

      const type = vehicleTypeData.find((v) => v.id == values.vehicleType);
      const model = type?.models.find((m: any) => m.id == values.vehicleModel);
      const priceListObj: Record<string, number> = {};
      if (model && values.selectedPriceItems) {
        values.selectedPriceItems.forEach((key: string) => {
          priceListObj[key] = parseFloat(model[key] || 0);
        });
      }

      const isBatapola = values.isBatapola || false;
      // Temporary override: disable process, just save the value
      const processBatapola = false; // isBatapola
      if (processBatapola) {
        let subtotal = 0;
        Object.values(priceListObj).forEach((val) => (subtotal += val));
        const dp = parseFloat(values.downpayment) || 0;
        const initialBalance = subtotal - dp;
        const threshold = config.batapolaThreshold || 500000;
        const flatFee = config.batapolaFlatFee || 25000;
        const rate = config.batapolaRate || 0.05;

        let docCharge = 0;
        if (initialBalance >= threshold - flatFee) {
          docCharge = flatFee;
        } else if (initialBalance > 0) {
          docCharge = (initialBalance / (1 - rate)) * rate;
        }
        priceListObj["document_charge"] = docCharge;
      }

      const inquiryData = {
        ...values,
        priceList: priceListObj,
        isBatapola: isBatapola,
      };

      if (editingNewInquiryIndex !== null) {
        newInq[editingNewInquiryIndex] = inquiryData;
      } else {
        newInq.push(inquiryData);
      }

      form.setFieldsValue({ inquiries: newInq });
      setNewInquiryModalOpen(false);
    });
  };

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleFinish}
      className="space-y-4"
    >
      <Form.Item name="inquiries" hidden initialValue={[]} />
      <Card title="Customer Details" className="shadow-md">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {isEditMode ? (
            <>
              <Form.Item label="Name">
                <p className="font-medium">{initialValues?.name || "-"}</p>
              </Form.Item>
              <Form.Item label="Contact Number">
                <p className="font-medium">{initialValues?.mobile || "-"}</p>
              </Form.Item>
            </>
          ) : (
            <>
              <Form.Item
                required
                label="Name"
                name="name"
                rules={[{ required: true }]}
              >
                <Input placeholder="Name" />
              </Form.Item>
              <Form.Item
                required
                label="Contact Number"
                name="mobile"
                rules={[{ required: true }]}
              >
                <Input placeholder="Contact number" />
              </Form.Item>
            </>
          )}
        </div>

        {isEditMode && existingInquiries.length > 0 && (
          <div className="mt-4">
            <h3 className="font-semibold mb-2">Existing Inquiries</h3>
            <Table
              columns={inquiryColumns}
              dataSource={existingInquiries}
              rowKey="id"
              pagination={false}
              bordered
              className="overflow-x-auto"
            />
          </div>
        )}
      </Card>

      <Card
        title={
          <div className="flex justify-between items-center">
            <span>New Inquiries</span>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setEditingNewInquiryIndex(null);
                setNewInquiryModalOpen(true);
                newInquiryForm.resetFields();
                setModalAvailableVehicles([]);
              }}
            >
              Add New Inquiry
            </Button>
          </div>
        }
        className="shadow-md"
      >
        <Table
          dataSource={watchedInquiries || []}
          rowKey={(record) => record.inquiryNumber || Math.random()}
          columns={columns}
          pagination={false}
          locale={{ emptyText: "No new inquiries added" }}
          className="overflow-x-auto"
        />
      </Card>

      {!isEditMode && (
        <div className="flex items-center justify-end gap-3 pt-4 border-t sticky bottom-0 bg-white p-4 shadow-top z-10">
          <Button onClick={handleFinish} type="default">
            Cancel
          </Button>
          <Button
            type="primary"
            htmlType="submit"
            disabled={watchedInquiries?.length === 0}
            loading={submitting}
            size="large"
          >
            Save Customer & Inquiries
          </Button>
        </div>
      )}

      <Drawer
        title={
          editingNewInquiryIndex !== null
            ? "Edit New Inquiry"
            : "Add New Inquiry"
        }
        open={newInquiryModalOpen}
        onClose={() => setNewInquiryModalOpen(false)}
        width={800}
      >
        <Form form={newInquiryForm} layout="vertical" onFinish={addnquery}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Form.Item
              label="Inquiry Number"
              name="inquiryNumber"
              rules={[{ required: true }]}
            >
              <Input placeholder="Inquiry number" />
            </Form.Item>
            <Form.Item
              label="Dealer"
              name="dealer_id"
              rules={[{ required: true }]}
            >
              <Select
                placeholder="Select Dealer"
                loading={isFetchingDealers}
                allowClear
                optionFilterProp="children"
                showSearch
                onSelect={(value) => {
                  setSelectedDealer(value);
                }}
              >
                {dealers.map((d: any) => (
                  <Option key={d.id} value={d.id}>
                    {d.name} {d.location ? `- ${d.location}` : ""}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              label="Sales Person"
              name="salesPersonId"
              rules={[{ required: true }]}
            >
              <Select
                placeholder="Select Sales Person"
                loading={isFetchingSalesPersons}
                allowClear
                optionFilterProp="children"
                showSearch
              >
                {salesPersons.map((sp: any) => (
                  <Option key={sp.id} value={sp.id}>
                    {sp.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              label="Vehicle Type"
              name="vehicleType"
              rules={[{ required: true }]}
            >
              <Select
                onChange={(val) => {
                  newInquiryForm.setFieldsValue({
                    vehicleModel: undefined,
                    vehicleId: undefined,
                    selectedPriceItems: [],
                    total: 0,
                  });
                  setModalAvailableVehicles([]);
                }}
              >
                {vehicleTypeData?.map((t) => (
                  <Option key={t.id} value={t.id}>
                    {t.name || t.v_type}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              label="Vehicle Model"
              name="vehicleModel"
              rules={[{ required: true }]}
            >
              <Select
                onChange={(val) => {
                  const typeId = newInquiryForm.getFieldValue("vehicleType");
                  newInquiryForm.setFieldsValue({
                    vehicleId: undefined,
                    selectedPriceItems: [],
                    total: 0,
                  });
                  fetchVehiclesForNewInquiry(typeId, val);
                }}
              >
                {vehicleTypeData
                  .find(
                    (v) => v.id === newInquiryForm.getFieldValue("vehicleType"),
                  )
                  ?.models.map((m: any) => (
                    <Option key={m.id} value={m.id}>
                      {m.name}
                    </Option>
                  ))}
              </Select>
            </Form.Item>
            <Form.Item label="Assign Specific Vehicle" name="vehicleId">
              <Select
                placeholder="Select vehicle"
                allowClear
                loading={isFetchingModalVehicles}
                optionLabelProp="label"
              >
                {modalAvailableVehicles.map((v: any) => (
                  <Option
                    key={v.id}
                    value={v.id}
                    label={`${v.engine_number} (${v.color_name})`}
                  >
                    <div className="flex flex-col">
                      <span className="font-semibold">
                        ENG: {v.engine_number}
                      </span>
                      <span className="text-xs">
                        CHAS: {v.chassis_number} | Color: {v.color_name}
                      </span>
                    </div>
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </div>

          <Form.Item
            name="selectedPriceItems"
            label="Pricing Components"
            initialValue={[]}
          >
            <Checkbox.Group
              className="w-full"
              onChange={() => calculateNewInquiryTotal()}
            >
              <Row gutter={[16, 16]}>
                {[
                  "price",
                  "transport_charges",
                  "registration_cost_lease",
                  "registration_cost_cash",
                  "insurance_cost",
                  "special_number_level_1",
                  "special_number_level_2",
                  "special_number_level_3",
                  "special_number_level_4",
                  "special_number_level_5",
                  "rajya_pranama",
                  "age_stock",
                  "discount",
                  "document_charge",
                ].map((field) => {
                  const typeId = newInquiryForm.getFieldValue("vehicleType");
                  const modelId = newInquiryForm.getFieldValue("vehicleModel");
                  const modelData = vehicleTypeData
                    .find((v) => v.id === typeId)
                    ?.models.find((m: any) => m.id === modelId);

                  if (!modelData) return null;

                  let amount = parseFloat(modelData?.[field] || 0);
                  if (field === "document_charge") {
                    const isBatapola =
                      newInquiryForm.getFieldValue("isBatapola");
                    // Temporary override: disable process
                    const processBatapola = false; // isBatapola
                    if (!processBatapola) return null;
                    // Calculate current subtotal to show document charge
                    let subtotal = 0;
                    const selected =
                      newInquiryForm.getFieldValue("selectedPriceItems") || [];
                    selected.forEach((key: string) => {
                      if (modelData[key] && key !== "document_charge")
                        subtotal += parseFloat(modelData[key]);
                    });
                    const dp =
                      parseFloat(newInquiryForm.getFieldValue("downpayment")) ||
                      0;
                    const gap = subtotal - dp;
                    const threshold = config.batapolaThreshold || 500000;
                    const flatFee = config.batapolaFlatFee || 25000;
                    const rate = config.batapolaRate || 0.05;

                    if (gap >= threshold - flatFee) {
                      amount = flatFee;
                    } else if (gap > 0) {
                      amount = (gap / (1 - rate)) * rate;
                    } else {
                      amount = 0;
                    }
                  }

                  return (
                    <Col span={12} md={8} key={field}>
                      <Checkbox
                        value={field}
                        disabled={field === "document_charge"}
                      >
                        {field.replace(/_/g, " ")}:{" "}
                        <span className="text-gray-500">
                          {isCalculatingNew && field === "document_charge" ? (
                            <LoadingOutlined />
                          ) : (
                            parseFloat(amount.toFixed(2)).toLocaleString()
                          )}
                        </span>
                      </Checkbox>
                    </Col>
                  );
                })}
              </Row>
            </Checkbox.Group>
          </Form.Item>

          <Form.Item
            label="Is Batapola"
            name="isBatapola"
            valuePropName="checked"
            initialValue={false}
          >
            {/* <Switch onChange={() => calculateNewInquiryTotal()} /> */}
            <Checkbox
              onChange={(e) => {
                calculateNewInquiryTotal();
              }}
            />
          </Form.Item>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Form.Item
              label="Total Amount"
              name="total"
              rules={[{ required: true }]}
            >
              <InputNumber
                className="w-full"
                readOnly
                prefix="Rs."
                formatter={(value) =>
                  `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                }
              />
            </Form.Item>

            {watchedNewInquiryTotal > 0 && (
              <>
                <Form.Item
                  label="Down payment (including document charge)"
                  name="downpayment"
                  rules={[{ required: true }]}
                  initialValue={0}
                >
                  <InputNumber
                    className="w-full"
                    prefix="Rs."
                    onChange={() => calculateNewInquiryTotal()}
                    formatter={(value) =>
                      `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                    }
                  />
                </Form.Item>

                <Form.Item
                  label="Loan Amount"
                  name="loanamount"
                  initialValue={0}
                >
                  <InputNumber
                    className="w-full"
                    readOnly
                    prefix="Rs."
                    formatter={(value) =>
                      `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                    }
                  />
                </Form.Item>
              </>
            )}
          </div>

          <Button
            className={"float-right"}
            type="primary"
            htmlType="submit"
            disabled={watchedNewInquiryVehicle && !watchedNewInquiryTotal}
          >
            Add Inquiry
          </Button>
        </Form>
      </Drawer>

      <Drawer
        title={`Edit Inquiry: ${editingInquiry?.inquiryNumber}`}
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        width={800}
        footer={null}
        destroyOnClose
      >
        <Form form={editForm} layout="vertical" onFinish={handleUpdateInquiry}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Form.Item
              label="Inquiry Number"
              name="inquiryNumber"
              rules={[{ required: true }]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              label="Status"
              name="status"
              rules={[{ required: true }]}
            >
              <Select>
                <Option value="Paid">Paid</Option>
                <Option value="Pending">Pending</Option>
                <Option value="Cancelled">Cancelled</Option>
              </Select>
            </Form.Item>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Form.Item label="Sales Person" name="salesPersonId">
              <Select
                placeholder="Select Sales Person"
                loading={isFetchingSalesPersons}
                allowClear
                optionFilterProp="children"
                showSearch
              >
                {salesPersons.map((sp: any) => (
                  <Option key={sp.id} value={sp.id}>
                    {sp.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Form.Item
              label="Vehicle Type"
              name="vehicleType"
              rules={[{ required: true }]}
            >
              <Select
                onChange={(val) => {
                  editForm.setFieldsValue({
                    vehicleModel: undefined,
                    vehicleId: undefined,
                    selectedPriceItems: [],
                    totalamount: 0,
                  });
                  setEditAvailableVehicles([]);
                }}
              >
                {vehicleTypeData?.map((t) => (
                  <Option key={t.id} value={t.id}>
                    {t.name || t.v_type}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              label="Vehicle Model"
              name="vehicleModel"
              rules={[{ required: true }]}
            >
              <Select
                onChange={(val) => {
                  const typeId = editForm.getFieldValue("vehicleType");
                  editForm.setFieldsValue({
                    vehicleId: undefined,
                    selectedPriceItems: [],
                    totalamount: 0,
                  });
                  fetchVehiclesForEdit(typeId, val);
                }}
              >
                {vehicleTypeData
                  .find((v) => v.id === editForm.getFieldValue("vehicleType"))
                  ?.models.map((m: any) => (
                    <Option key={m.id} value={m.id}>
                      {m.name}
                    </Option>
                  ))}
              </Select>
            </Form.Item>
          </div>

          <Form.Item label="Assign Specific Vehicle" name="vehicleId">
            <Select
              placeholder="Select vehicle..."
              allowClear
              loading={isFetchingEditVehicles}
              optionLabelProp="label"
            >
              {editAvailableVehicles.map((v: any) => (
                <Option
                  key={v.id}
                  value={v.id}
                  label={`${v.engine_number} (${v.color_name})`}
                >
                  <div className="flex flex-col">
                    <span className="font-semibold">
                      ENG: {v.engine_number}
                    </span>
                    <span className="text-xs">
                      CHAS: {v.chassis_number} | Color: {v.color_name} |{" "}
                    </span>
                  </div>
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label="Total Amount"
            name="totalamount"
            rules={[{ required: true }]}
          >
            <InputNumber
              className="w-full"
              readOnly
              prefix="Rs."
              formatter={(value) =>
                `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
              }
            />
          </Form.Item>

          {watchedEditInquiryTotal > 0 && (
            <>
              <Form.Item
                label="Down payment (including document charge)"
                name="downpayment"
                rules={[{ required: true }]}
                initialValue={0}
              >
                <InputNumber
                  className="w-full"
                  prefix="Rs."
                  onChange={() => calculateEditTotal()}
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                />
              </Form.Item>

              <Form.Item label="Loan Amount" name="loanamount" initialValue={0}>
                <InputNumber
                  className="w-full"
                  readOnly
                  prefix="Rs."
                  formatter={(value) =>
                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                  }
                />
              </Form.Item>
            </>
          )}

          <Form.Item
            label="Is Batapola"
            name="isBatapola"
            valuePropName="checked"
          >
            {/* <Switch onChange={() => calculateEditTotal()} /> */}
            <Checkbox
              onChange={(e) => {
                calculateEditTotal();
              }}
            />
          </Form.Item>

          {editForm.getFieldValue("vehicleModel") && (
            <div className="p-3 bg-gray-50 border rounded mb-4">
              <p className="font-semibold mb-2">Pricing Components:</p>
              <Form.Item name="selectedPriceItems" className="mb-0">
                <Checkbox.Group
                  className="w-full"
                  onChange={calculateEditTotal}
                >
                  <Row gutter={[16, 16]}>
                    {[
                      "price",
                      "transport_charges",
                      "registration_cost_lease",
                      "registration_cost_cash",
                      "insurance_cost",
                      "special_number_level_1",
                      "special_number_level_2",
                      "special_number_level_3",
                      "special_number_level_4",
                      "special_number_level_5",
                      "rajya_pranama",
                      "age_stock",
                      "discount",
                      "document_charge",
                    ].map((field) => {
                      // Safely get model data for edit form
                      const type = vehicleTypeData.find(
                        (v) => v.id === editForm.getFieldValue("vehicleType"),
                      );
                      const model = type?.models.find(
                        (m: any) =>
                          m.id === editForm.getFieldValue("vehicleModel"),
                      );
                      let amount = parseFloat(model?.[field] || 0);

                      if (field === "document_charge") {
                        const isBatapola = editForm.getFieldValue("isBatapola");
                        // Temporary override: disable process
                        const processBatapola = false; // isBatapola
                        if (!processBatapola) return null;
                        // Calculate current subtotal to show document charge
                        let subtotal = 0;
                        const selected =
                          editForm.getFieldValue("selectedPriceItems") || [];
                        selected.forEach((key: string) => {
                          if (model[key] && key !== "document_charge")
                            subtotal += parseFloat(model[key]);
                        });
                        const dp =
                          parseFloat(editForm.getFieldValue("downpayment")) ||
                          0;
                        const gap = subtotal - dp;
                        const threshold = config.batapolaThreshold || 500000;
                        const flatFee = config.batapolaFlatFee || 25000;
                        const rate = config.batapolaRate || 0.05;

                        if (gap >= threshold - flatFee) {
                          amount = flatFee;
                        } else if (gap > 0) {
                          amount = (gap / (1 - rate)) * rate;
                        } else {
                          amount = 0;
                        }
                      }

                      return (
                        <Col span={12} md={6} key={field}>
                          <Checkbox
                            value={field}
                            disabled={field === "document_charge"}
                          >
                            {field.replace(/_/g, " ")}:{" "}
                            <span className="text-gray-500">
                              {isCalculatingEdit &&
                              field === "document_charge" ? (
                                <LoadingOutlined />
                              ) : (
                                parseFloat(amount.toFixed(2)).toLocaleString()
                              )}
                            </span>
                          </Checkbox>
                        </Col>
                      );
                    })}
                  </Row>
                </Checkbox.Group>
              </Form.Item>
            </div>
          )}

          <div className="flex justify-end gap-2 mt-4 pt-4 border-t">
            <Button onClick={() => setEditModalOpen(false)}>Cancel</Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={updateLoading}
              disabled={watchedEditInquiryVehicle && !watchedEditInquiryTotal}
            >
              Update Inquiry
            </Button>
          </div>
        </Form>
      </Drawer>
    </Form>
  );
};
